import React, { useState, useRef, useEffect } from 'react';
import { TextField, Paper, ClickAwayListener, IconButton, InputAdornment } from '@mui/material';
import { RestApi } from '../../../common/services/rest-api.service';
import { useHistory, useLocation } from 'react-router-dom';
import { Common } from '../../../common/services/common.service';
import Lottie from 'lottie-react-web';
import LoadingAnimationData from './gradient text animation.json';
import { setDelay, showAiAlert } from 'features/AlertSlice';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { IReduxState } from 'app/store';
import useLocalStorage from 'common/hooks/useLocalStorage';
import { Amplitude } from 'common/services/amplitude.service';
import './AiSearchBar.scss';

const SUGGESTED_QUERIES = [
  {
    title: 'Show critical hijackable assets in open status',
    link:
      '/pages/actionItems/open?filters={"urgency__in":"critical","tags__contains":"Hijackable","current_state__in":"open"}'
  },
  {
    title: '.com domains tagged as exploitable',
    link: '/pages/discovery/internalAssets?filters={"type":"0","asset__endswith":".com","tags__contains":"Exploitable"}'
  },
  {
    title: 'Find 3rd party script dependencies',
    link: '/pages/discovery/digitalSupplyChain?filters={"connections_types__in":"0"}'
  }
];

export function AiSearchBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestedQueries, setSuggestedQueries] = useState(SUGGESTED_QUERIES);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { filtersSuffix } = useSelector((state: IReduxState) => state.whitelist);
  const dispatch = useDispatch<any>();
  const { setStoredValue: setStorageSearch } = useLocalStorage('ai_search');
  const history = useHistory();
  const location = useLocation();

  const isMac = navigator.platform.toUpperCase().includes('MAC');

  useEffect(() => {
    const getSuggestedQueries = () => {
      try {
        const queryUrl = 'ionix-ai/predefined';
        RestApi.getData(queryUrl).subscribe(
          (response: any) => {
            setSuggestedQueries(response);
          },
          error => {
            console.error('Error querying IONIX AI:', error);
          }
        );
      } catch (error) {
        console.log(error);
      }
    };
    getSuggestedQueries();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('is_ai_alert') && queryParams.get('is_ai_alert') === 'true') {
      queryParams.delete('is_ai_alert');
      history.replace({
        search: queryParams.toString()
      });
      dispatch(setDelay(10000));
      dispatch(
        showAiAlert({
          variant: 'sparkle',
          title: 'Filters Updated',
          text: 'IONIX AI created this filtered view for you.'
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCmdK = isMac && e.metaKey && e.key.toLowerCase() === 'k';
      const isCtrlK = !isMac && e.ctrlKey && e.key.toLowerCase() === 'k';

      if (isCmdK || isCtrlK) {
        e.preventDefault();
        handleOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMac]);

  const loadingAnimationDefaultOptions = {
    loop: true,
    animationData: LoadingAnimationData
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setHasError(false);
    setQuery('');
  };

  const getUrl = (link: string) => {
    const account = Common.getParamFromUrl('account');
    const accountAddition = account ? `&account=${account}` : '';
    const url_to_open = `${link}${accountAddition}`;
    return encodeURI(url_to_open);
  };

  const onSelectUrl = (link: string) => {
    const url = getUrl(link);

    if (window.location.pathname === url.split('?')[0]) {
      window.location.href = url.includes('?') ? `${url}&is_ai_alert=true` : `${url}?is_ai_alert=true`;
    } else {
      history.push(url.includes('?') ? `${url}&is_ai_alert=true` : `${url}?is_ai_alert=true`);
    }
    handleClose();
    setIsLoading(false);
  };

  const mapUrl = {
    '/pages/actionItems/open': 'remediation/action-items/open/',
    '/pages/actionItems/closed': 'remediation/action-items/closed/',
    '/pages/actionItems/alerts': 'alerts/',
    '/pages/discovery/internalAssets': 'discovery/org-assets/',
    '/pages/discovery/digitalSupplyChain': 'discovery/digital-supply-chain/',
    '/pages/discovery/publicCloud': 'discovery/cloud/',
    '/pages/discovery/loginAssets': 'discovery/logins/',
    '/pages/discovery/managedDomains': 'discovery/managed-domains/',
    '/pages/discovery/certificates': 'discovery/certificates/',
    '/pages/discovery/unlinkedAssets': 'discovery/unlinked-assets/',
    '/pages/discovery/mobileAssets': 'discovery/mobile/',
    '/pages/assessments/internalAssets': 'assessments/org-assets/',
    '/pages/assessments/digitalSupplyChain': 'assessments/digital-supply-chain/',
    '/pages/activeProtection': 'remediation/active-protection/'
  };

  const buildUrlFilters = (filters: any) => {
    try {
      const _f = JSON.parse(filters);
      const buildF = Object.keys(_f).reduce((all: string, key: string, index: number) => {
        return `${all}${key}=${_f[key]}${index !== Object.keys(_f).length - 1 ? '&' : ''}`;
      }, '?');
      return buildF !== '?' ? `${buildF}&limit=1` : `?limit=1`;
    } catch (err) {
      console.log('# buildUrlFilters Error');
    }
  };

  const areValidFiltersSuffixes = (filters: string) => {
    try {
      const _filters = JSON.parse(filters);
      const keys = Object.keys(_filters);

      if (!keys.length) {
        return true;
      }

      const allFilters = keys.reduce((all: string[], curr: string) => {
        const currValue = curr.split('__').length > 1 ? `__${curr.split('__')[1]}` : '';
        return [...all, currValue];
      }, []);

      return [...new Set(allFilters)].every((item: string) => {
        return filtersSuffix.includes(item);
      });
    } catch (err) {
      console.log('#SearchBarAI Err, filters: ', filters);
      return false;
    }
  };

  const handleQuery = async (q: string) => {
    if (!q.trim()) return;
    setHasError(false);
    setIsLoading(true);

    try {
      const queryUrl = `ionix-ai/query?query=${encodeURIComponent('filter:' + q.trim())}`;
      RestApi.getData(queryUrl).subscribe(
        (response: any) => {
          const filtersParam = response.split('filters=')[1] || '';
          const key = response.split('?')[0] as keyof typeof mapUrl;
          const url = `${mapUrl[key]}${!!filtersParam ? buildUrlFilters(filtersParam) : ''}`;

          if (!mapUrl[key]) {
            console.log('#SearchBarAI, URL Health check: did not find any URL key', key);
            return;
          }
          RestApi.getData(url).subscribe(
            (_response: any) => {
              if (!areValidFiltersSuffixes(filtersParam)) {
                console.log('#SearchBarAI, not valid suffix: ', url, response);

                setIsLoading(false);
                setHasError(true);
                return;
              }
              Amplitude.save('AI_SEARCH', { search: q.trim(), url: response });
              onSelectUrl(response);
            },
            error => {
              console.log('#SearchBarAI, URL Health check: ', url, error);
              setIsLoading(false);
              setHasError(true);
            }
          );
        },
        error => {
          setIsLoading(false);
          setHasError(true);
        }
      );
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleQuery(query);
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };
  const fakeLoader = (link: string) => {
    setIsLoading(true);
    const timeout = setTimeout(() => {
      onSelectUrl(link.includes('?') ? `${link}&is_ai_alert=true` : `${link}?is_ai_alert=true`);
      clearTimeout(timeout);
    }, 2000);
  };

  const handleOnChange = (ev: any) => {
    setStorageSearch(ev.target.value);
    setQuery(ev.target.value);
  };

  return (
    <div className="ionix-ai-search-bar">
      {!open ? (
        <div className="collapsed-bar" onClick={handleOpen} tabIndex={0} role="button">
          <img className="icon" src="/assets/images/sparkle.svg" alt="AI" width="14" height="14" />
          <span className="placeholder">Find it with IONIX AI</span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/images/beta.svg" alt="enter" width="25px" height="14px" />
            <span className="shortcut" style={{ display: 'flex', alignItems: 'center' }}>
              {isMac ? (
                <span style={{ fontSize: 12, paddingRight: 2 }}>⌘</span>
              ) : (
                <span style={{ fontSize: 12, paddingRight: 2 }}>Ctrl</span>
              )}
              <span style={{ fontSize: 12 }}>{`+ K`}</span>
            </span>
          </span>
        </div>
      ) : (
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className="expanded-bar" elevation={4}>
            <div className={classNames('input-row', { loading: isLoading })}>
              <TextField
                inputRef={inputRef}
                autoFocus
                fullWidth
                variant="outlined"
                size="small"
                value={query}
                onChange={handleOnChange}
                onKeyDown={handleInputKeyDown}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img className="icon" src="/assets/images/sparkle.svg" alt="AI" width="14" height="14" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleQuery(query)} className="shortcut end-icon">
                        <img src="/assets/images/enter.svg" alt="enter" width="12px" height="12px" />
                      </IconButton>
                      <IconButton onClick={handleClose} edge="end" className="end-icon">
                        <img
                          src="/assets/images/x.svg"
                          onClick={handleClose}
                          alt="Close"
                          width="12px"
                          height="12px"
                          className="close"
                        />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </div>
            {hasError && (
              <span className="error-message">{'Sorry, I cannot do that yet.\nTry using other terms.'}</span>
            )}
            {isLoading && (
              <div className="ai-loading">
                <Lottie options={loadingAnimationDefaultOptions} height="20px" />
              </div>
            )}
            {!isLoading && !hasError && (
              <div className="suggested-queries">
                {suggestedQueries.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="suggestion"
                    onClick={() => fakeLoader(suggestion.link)}
                    tabIndex={0}
                    role="button"
                  >
                    {suggestion.title}
                  </div>
                ))}
              </div>
            )}
          </Paper>
        </ClickAwayListener>
      )}
    </div>
  );
}
